import { matchPath, useLocation } from 'react-router-dom';
import { v2 } from '../paths';

/** Where the page stands: which customer and environment, read from the URL so every link carries it. */
export interface Scope {
    customer?: string;
    environment?: string;
    /** The part of the app, for the rail and the last crumb. */
    section: 'home' | 'environments' | 'issues' | 'deploy' | 'exact' | 'scenarios' | 'learn' | 'env' | 'monitor' | 'errorlog' | 'issue' | 'run';
    /** True on the tabs of one environment (/v2/env/...), where switching environment keeps the tab. */
    inEnv: boolean;
    issueKey?: string;
    /** The object whose dossier is open. */
    objectId?: string;
    runId?: string;
}

const PATTERNS: { pattern: string; section: Scope['section']; inEnv?: boolean }[] = [
    { pattern: '/v2/env/:customer/:environment/issues', section: 'issues', inEnv: true },
    { pattern: '/v2/env/:customer/:environment/monitor', section: 'monitor', inEnv: true },
    { pattern: '/v2/env/:customer/:environment/foutenlogboek', section: 'errorlog', inEnv: true },
    { pattern: '/v2/env/:customer/:environment/exact', section: 'exact', inEnv: true },
    { pattern: '/v2/env/:customer/:environment/object/:objectId', section: 'env', inEnv: true },
    { pattern: '/v2/env/:customer/:environment', section: 'env', inEnv: true },
    { pattern: '/v2/issue/:customer/:environment/:key', section: 'issue' },
    { pattern: '/v2/exact/:customer/:environment', section: 'exact' },
    { pattern: '/v2/scenarios/:customer/:environment/:runId', section: 'run' },
    { pattern: '/v2/omgevingen', section: 'environments' },
    { pattern: '/v2/issues', section: 'issues' },
    { pattern: '/v2/deploy/*', section: 'deploy' },
    { pattern: '/v2/exact', section: 'exact' },
    { pattern: '/v2/scenarios', section: 'scenarios' },
    { pattern: '/v2/leren/*', section: 'learn' },
];

export const useScope = (): Scope => {
    const { pathname } = useLocation();

    for (const { pattern, section, inEnv } of PATTERNS) {
        const match = matchPath(pattern, pathname);
        if (match) {
            return {
                section,
                inEnv: !!inEnv,
                customer: match.params.customer,
                environment: match.params.environment,
                issueKey: match.params.key,
                objectId: match.params.objectId,
                runId: match.params.runId,
            };
        }
    }

    return { section: 'home', inEnv: false };
};

/** The same tab of another environment, so switching from Monitor lands on Monitor. */
export const sameTab = (scope: Scope, customer: string, environment: string): string => {
    switch (scope.section) {
        case 'monitor':
            return v2.monitor(customer, environment);
        case 'errorlog':
            return v2.errorLog(customer, environment);
        case 'issues':
            return v2.envIssues(customer, environment);
        case 'exact':
            return v2.envExact(customer, environment);
        default:
            return v2.env(customer, environment);
    }
};

export const SECTION_LABEL: Record<Scope['section'], string> = {
    home: 'Dashboard',
    environments: 'Omgevingen',
    issues: 'Issues',
    deploy: 'Deploy',
    exact: 'Exact-verbruik',
    scenarios: "Scenario's",
    learn: 'Leren',
    env: 'Live',
    monitor: 'Monitor',
    errorlog: 'Foutenlogboek',
    issue: 'Issue',
    run: 'Run',
};
